import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { shellKeys } from '@app/api/hooks/shell';
import { del, get, post, put, withPath, type ApiBody, type ApiData } from '@app/api/typed';
import type { components, operations } from '@app/api/types';
import { PAGE_SIZE } from '@zarya/ui';
import { readingInterval } from '@app/reading';

/**
 * Hooks de T4/T5 (identidades). O contrato está congelado: `listIdentities` e
 * `listIdentityRoles` são as leituras da tela T4; o `runDiscovery` do botão mora
 * em `@app/discovery` desde 29/08.
 */

export type IdentitiesPage = ApiData<'/api/v1/identities'>;
export type Identity = components['schemas']['Identity'];
export type IdentityRole = components['schemas']['IdentityRole'];
export type EstadoIdentidade = components['schemas']['EstadoIdentidade'];
export type IdentityAssign = ApiBody<'/api/v1/identities/{id}', 'put'>;
export type IdentityImpact = ApiData<'/api/v1/identities/{id}/impact'>;
export type InfraConfig = ApiData<'/api/v1/infra/config'>;
export type SpiffeStatus = ApiData<'/api/v1/identities/{id}/spiffe'>;
/**
 * A prévia do «Ambiente efetivo» de T5 (Lote 3 · F0-b). Vem do servidor porque a
 * precedência da D-6 tem quatro elos e o navegador só enxerga dois — ver o
 * cabeçalho de `@app/ambiente`.
 */
export type EnvironmentPreview = ApiData<'/api/v1/identities/{id}/environment-preview'>;
/**
 * A prévia do SPIFFE ID de T5 (aim-xsk.7). Vem do servidor, pelo template do site:
 * a tela não compõe o ID.
 */
export type SpiffePreview = ApiData<'/api/v1/identities/{id}/spiffe-preview'>;
export type IdentityCapabilities = ApiData<'/api/v1/identities/{id}/capabilities'>;
export type IdentityPolicy = components['schemas']['Policy'];
export type Release = components['schemas']['Release'];
export type ReleaseDetail = ApiData<'/api/v1/identities/{id}/releases/{release_id}'>;
export type TenantConfig = ApiData<'/api/v1/tenant'>;

/** Filtros de T4 (FR-053). `papeis` vazio = todos. */
/**
 * Ordenações que o contrato aceita em `listIdentities` (`sort`, enum fechado).
 * A tela não pode oferecer nenhuma outra: valor fora deste conjunto é recusado
 * pelo servidor, e a coluna que a oferecesse mentiria.
 */
export const SORTINGS = ['slug', '-slug', 'discovered_at', '-discovered_at', 'state'] as const;
export type SortingIdentities = (typeof SORTINGS)[number];

/**
 * Recorte de T4 entre o que o operador governa e o que a varredura achou (30/08).
 * `null` = as duas.
 */
export type ScopeIdentities = NonNullable<
  NonNullable<operations['listIdentities']['parameters']['query']>['scope']
>;

export interface IdentitiesFilterValues {
  q: string;
  papeis: string[];
  state: EstadoIdentidade | null;
  /** `null` = as duas metades; a tela abre em `governadas`. */
  scope: ScopeIdentities | null;
  /**
   * Slug do site (P1). `null` = todos.
   *
   * Vai ao SERVIDOR, e é por isso que ele está no `queryKey`: a lista pagina, e um
   * recorte feito depois de ler mostraria as linhas certas com o `total` errado.
   */
  site: string | null;
  /**
   * Slug do ambiente do cadastro (Lote 3 · F1-c). `null` = todos.
   *
   * Vai ao SERVIDOR, como o `site` e pela mesma razão: a lista pagina, e recortar
   * depois de ler mostraria as linhas certas com o `total` errado.
   */
  environment: string | null;
  /** `null` = a ordem padrão do servidor (`slug`), que não vai na query. */
  sort: SortingIdentities | null;
  page: number;
}

/**
 * **A tela abre em «governadas», e isso é decisão de leitura, não de dado**
 * (ensaio de 30/08).
 *
 * A varredura de um cluster real não traz só agentes: no laboratório, 16 das 21
 * linhas eram workloads do `segura-agent-lab`, todas em «Descoberta · sem
 * atribuição» e todas em laranja. Quem abria a tela procurava os cinco que
 * governa no meio das dezesseis que não governa.
 *
 * Nada fica escondido: as pílulas mostram a contagem dos dois lados
 * (`meta.governadas` / `meta.descobertas`), e «Descobertas» fica a um clique.
 */
export const FILTERS_INICIAIS: IdentitiesFilterValues = {
  q: '',
  papeis: [],
  state: null,
  scope: 'governadas',
  site: null,
  environment: null,
  sort: null,
  page: 1,
};

/**
 * Se há filtro **do operador** aplicado — o que decide o texto do vazio e o
 * botão «Limpar filtros».
 *
 * O `escopo` **não** entra: ele tem estado inicial diferente de "tudo", e contá-lo
 * como filtro faria a tela abrir anunciando que já está filtrando. O caminho de
 * volta dele é a própria pílula «Todas», que está sempre visível — não o botão de
 * limpar.
 */
export function hasFilter(filters: IdentitiesFilterValues): boolean {
  return (
    filters.q.trim() !== '' ||
    filters.papeis.length > 0 ||
    filters.state !== null ||
    filters.site !== null ||
    filters.environment !== null
  );
}

export const identitiesKeys = {
  lista: (filters: IdentitiesFilterValues) =>
    [
      'identities',
      'lista',
      filters.q,
      filters.papeis.join(','),
      filters.state,
      filters.scope,
      filters.site,
      filters.environment,
      filters.sort,
      filters.page,
    ] as const,
  todas: ['identities'] as const,
  item: (id: string) => ['identities', 'item', id] as const,
  impact: (id: string) => ['identities', 'impact', id] as const,
  papeis: ['identity-roles'] as const,
  spiffe: (id: string) => ['identities', 'spiffe', id] as const,
  // O que está NA TELA entra na chave: é o que faz a prévia acompanhar a
  // digitação, e o que deixa o react-query reaproveitar a resposta quando a
  // pessoa volta a um valor por que já passou.
  environmentPreview: (id: string, declared: string, namespace: string) =>
    ['identities', 'environment-preview', id, declared, namespace] as const,
  spiffePreview: (id: string, namespace: string, workload: string, serviceAccount: string) =>
    ['identities', 'spiffe-preview', id, namespace, workload, serviceAccount] as const,
  capabilities: (id: string) => ['identities', 'capabilities', id] as const,
  policies: (id: string) => ['identities', 'policies', id] as const,
  releases: (id: string) => ['identities', 'releases', id] as const,
  release: (id: string, releaseId: string) => ['identities', 'release', id, releaseId] as const,
};

/**
 * O contrato declara `papel` como `style: form, explode: false` — ou seja
 * `papel=a,b`, não o `papel[]=a&papel[]=b` que o axios monta por padrão. Este
 * serializador é o que mantém a chamada fiel ao contrato congelado.
 */
const listsByVirgula = {
  paramsSerializer: {
    serialize: (params: Record<string, unknown>) =>
      Object.entries(params)
        .filter(([, valor]) => {
          if (valor === undefined || valor === null || valor === '') return false;
          return !(Array.isArray(valor) && valor.length === 0);
        })
        .map(([chave, valor]) => {
          const texto = Array.isArray(valor) ? valor.join(',') : String(valor);
          return `${encodeURIComponent(chave)}=${encodeURIComponent(texto)}`;
        })
        .join('&'),
  },
};

/** Página de identidades de T4 (FR-053). */
export function useIdentities(
  filters: IdentitiesFilterValues,
): UseQueryResult<IdentitiesPage, ApiError> {
  return useQuery<IdentitiesPage, ApiError>({
    queryKey: identitiesKeys.lista(filters),
    queryFn: () =>
      get('/api/v1/identities', {
        params: {
          q: filters.q.trim() || undefined,
          role: filters.papeis.length ? filters.papeis : undefined,
          state: filters.state ?? undefined,
          scope: filters.scope ?? undefined,
          site: filters.site ?? undefined,
          environment: filters.environment ?? undefined,
          sort: filters.sort ?? undefined,
          page: filters.page,
          size: PAGE_SIZE,
        },
        config: listsByVirgula,
      }),
    // Digitar na busca não deve piscar o esqueleto a cada tecla: a página
    // anterior fica na tela até a nova chegar (FR-126).
    placeholderData: (previous) => previous,
    // A foto do cluster (aim-6ph.13): relê enquanto a leitura nova corre, e a coluna «Estado» se atualiza sozinha.
    refetchInterval: (query) => readingInterval(query.state.data?.meta?.reading),
  });
}

/** Identidade do drawer T5 (FR-045). */
export function useIdentity(id: string | null): UseQueryResult<Identity, ApiError> {
  return useQuery<Identity, ApiError>({
    queryKey: identitiesKeys.item(id ?? ''),
    queryFn: () =>
      get(withPath('/api/v1/identities/{id}', { id: id ?? '' }) as '/api/v1/identities/{id}'),
    enabled: Boolean(id),
    // A foto da identidade por trás do estado e do SecurityReady (aim-6ph.13): relê enquanto a leitura nova corre.
    refetchInterval: (query) => readingInterval(query.state.data?.reading),
  });
}

/**
 * Prévia do «Ambiente efetivo» enquanto T5 é editada (Lote 3 · F0-b).
 *
 * Só corre quando a tela DIFERE do gravado (`enabled`): sem edição, o valor e a
 * origem já vieram no `getIdentity`, e pedir de novo seria perguntar ao servidor
 * o que ele acabou de responder.
 *
 * `declared` e `namespace` vão SEMPRE, inclusive vazios — vazio quer dizer «a
 * pessoa apagou o campo», que é diferente de não mandar o parâmetro (aí valeria
 * o gravado, e a prévia mostraria o valor que ela acabou de apagar). É por isso
 * que nenhum dos dois vira `undefined` aqui.
 */
export function useEnvironmentPreview(
  id: string | null,
  onScreen: { declared: string; namespace: string },
  enabled: boolean,
): UseQueryResult<EnvironmentPreview, ApiError> {
  return useQuery<EnvironmentPreview, ApiError>({
    queryKey: identitiesKeys.environmentPreview(id ?? '', onScreen.declared, onScreen.namespace),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/environment-preview', {
          id: id ?? '',
        }) as '/api/v1/identities/{id}/environment-preview',
        { params: { declared: onScreen.declared, namespace: onScreen.namespace } },
      ),
    enabled: enabled && Boolean(id),
    // Digitar não deve piscar o campo: o valor anterior fica até o novo chegar.
    placeholderData: (previous) => previous,
  });
}

/**
 * A prévia do SPIFFE ID enquanto a pessoa digita (aim-xsk.7): o MESMO compositor
 * do servidor que grava e congela o ID. Antes a tela montava
 * `spiffe://<td>/ns/<ns>/sa/<sa>` por conta própria, ignorando o template do site.
 *
 * Mesma regra de `useEnvironmentPreview`: `namespace`, `workload` e `service_account`
 * vão SEMPRE, inclusive vazios. Desde 02/10 a consulta corre também sem edição: o
 * `template` da resposta é o que diz à tela se a Service Account é parte do ID.
 */
export function useSpiffePreview(
  id: string | null,
  onScreen: { namespace: string; workload: string; serviceAccount: string },
  enabled: boolean,
): UseQueryResult<SpiffePreview, ApiError> {
  return useQuery<SpiffePreview, ApiError>({
    queryKey: identitiesKeys.spiffePreview(
      id ?? '',
      onScreen.namespace,
      onScreen.workload,
      onScreen.serviceAccount,
    ),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/spiffe-preview', {
          id: id ?? '',
        }) as '/api/v1/identities/{id}/spiffe-preview',
        {
          params: {
            namespace: onScreen.namespace,
            workload: onScreen.workload,
            service_account: onScreen.serviceAccount,
          },
        },
      ),
    enabled: enabled && Boolean(id),
    placeholderData: (previous) => previous,
  });
}

/**
 * Trust domain do tenant, para a prévia do SPIFFE ID (FR-047). Mesma chave de
 * cache que T21 usa: os dois leem `getTenant`, e ler duas vezes seria desperdício.
 */
export function useTenantConfig(): UseQueryResult<TenantConfig, ApiError> {
  return useQuery<TenantConfig, ApiError>({
    queryKey: ['tenant'],
    queryFn: () => get('/api/v1/tenant'),
  });
}

/**
 * Configuração da infraestrutura — o grupo `registry` dá o `{registry}/{owner}`
 * que a imagem precisa respeitar (FR-046). Falhar aqui não trava a tela: sem a
 * configuração a validação local sai e quem recusa é o servidor.
 */
export function useInfraConfig(): UseQueryResult<InfraConfig, ApiError> {
  return useQuery<InfraConfig, ApiError>({
    queryKey: ['infra', 'config'],
    queryFn: () => get('/api/v1/infra/config'),
    retry: false,
  });
}

/**
 * **Salvar atribuição** (FR-049): vai ao executor e só grava depois do 2xx. A
 * resposta é a identidade efetiva — semeamos o cache com ela para a tela mostrar
 * o que ficou gravado, não o que foi enviado.
 */
export function useAssignIdentity(
  id: string,
): UseMutationResult<Identity, ApiError, IdentityAssign> {
  const queryClient = useQueryClient();
  return useMutation<Identity, ApiError, IdentityAssign>({
    mutationFn: (body) =>
      put(withPath('/api/v1/identities/{id}', { id }) as '/api/v1/identities/{id}', { body }),
    onSuccess: (identidade) => {
      queryClient.setQueryData(identitiesKeys.item(id), identidade);
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.todas });
      void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
      void queryClient.invalidateQueries({ queryKey: shellKeys.approvalsSummary });
    },
  });
}

/** Impacto da remoção — lido só quando a confirmação abre (FR-056, FR-130). */
export function useIdentityImpact(
  id: string,
  habilitado: boolean,
): UseQueryResult<IdentityImpact, ApiError> {
  return useQuery<IdentityImpact, ApiError>({
    queryKey: identitiesKeys.impact(id),
    queryFn: () =>
      get(withPath('/api/v1/identities/{id}/impact', { id }) as '/api/v1/identities/{id}/impact'),
    enabled: habilitado,
    gcTime: 0,
    refetchInterval: (query) => readingInterval(query.state.data?.pods_reading),
  });
}

/** **Remover** identidade (FR-056): executor primeiro, banco depois. */
export function useDeleteIdentity(id: string): UseMutationResult<unknown, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => del(withPath('/api/v1/identities/{id}', { id }) as '/api/v1/identities/{id}'),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: identitiesKeys.item(id) });
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.todas });
      void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
      void queryClient.invalidateQueries({ queryKey: shellKeys.approvalsSummary });
    },
  });
}

/**
 * Abas do drawer (T5, FR-054). Cada uma carrega **quando abre** (`habilitado`) e
 * mostra o próprio carregando/erro — uma aba fora do ar não derruba as outras
 * (FR-126).
 */
export function useIdentitySpiffe(
  id: string,
  habilitado: boolean,
): UseQueryResult<SpiffeStatus, ApiError> {
  return useQuery<SpiffeStatus, ApiError>({
    queryKey: identitiesKeys.spiffe(id),
    queryFn: () =>
      get(withPath('/api/v1/identities/{id}/spiffe', { id }) as '/api/v1/identities/{id}/spiffe'),
    enabled: habilitado,
    refetchInterval: (query) => readingInterval(query.state.data?.reading),
  });
}

export function useIdentityCapabilities(
  id: string,
  habilitado: boolean,
): UseQueryResult<IdentityCapabilities, ApiError> {
  return useQuery<IdentityCapabilities, ApiError>({
    queryKey: identitiesKeys.capabilities(id),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/capabilities', {
          id,
        }) as '/api/v1/identities/{id}/capabilities',
      ),
    enabled: habilitado,
    // A conferência com o PDP é a ÚLTIMA foto que a leitura da identidade grava (o L9 vem depois do L7 e do L10):
    // esta aba relê sozinha, mesmo depois de a gaveta já ter a foto do estado.
    refetchInterval: (query) => readingInterval(query.state.data?.pdp_reading),
  });
}

export function useIdentityPolicies(
  id: string,
  habilitado: boolean,
): UseQueryResult<IdentityPolicy[], ApiError> {
  return useQuery<IdentityPolicy[], ApiError>({
    queryKey: identitiesKeys.policies(id),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/policies', { id }) as '/api/v1/identities/{id}/policies',
      ),
    enabled: habilitado,
  });
}

export function useIdentityReleases(
  id: string,
  habilitado: boolean,
): UseQueryResult<ApiData<'/api/v1/identities/{id}/releases'>, ApiError> {
  return useQuery<ApiData<'/api/v1/identities/{id}/releases'>, ApiError>({
    queryKey: identitiesKeys.releases(id),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/releases', { id }) as '/api/v1/identities/{id}/releases',
      ),
    enabled: habilitado,
  });
}

/** Detalhe de uma release — declaração e procedência (FR-043). */
export function useIdentityRelease(
  id: string,
  releaseId: string | null,
): UseQueryResult<ReleaseDetail, ApiError> {
  return useQuery<ReleaseDetail, ApiError>({
    queryKey: identitiesKeys.release(id, releaseId ?? ''),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/releases/{release_id}', {
          id,
          release_id: releaseId ?? '',
        }) as '/api/v1/identities/{id}/releases/{release_id}',
      ),
    enabled: Boolean(releaseId),
  });
}

/** Criar papel na hora, pelo combobox de T5 (FR-052). */
export function useCreateIdentityRole(): UseMutationResult<IdentityRole, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<IdentityRole, ApiError, string>({
    mutationFn: (name) => post('/api/v1/identity-roles', { body: { name } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.papeis });
    },
  });
}

/** Catálogo de papéis — as pílulas de papel de T4 e o combobox de T5 (FR-052). */
export function useIdentityRoles(): UseQueryResult<IdentityRole[], ApiError> {
  return useQuery<IdentityRole[], ApiError>({
    queryKey: identitiesKeys.papeis,
    queryFn: () => get('/api/v1/identity-roles'),
  });
}

// O **Discovery agora** saiu daqui em 29/08, quando a varredura passou a correr
// fora da requisição (`202` + acompanhamento a cada 3 s). Disparo, polling,
// indicador, toast e invalidação viraram `@app/discovery` — a mesma peça para T3,
// T4, T21 e a moldura.
